import Vue from 'vue';
import axios from 'axios';
// TODO Remove dependancy to lodash
import camelCase from 'lodash/camelCase';

/**
 * Create string types used by doAsync and for data access in Vuex store
 * @param {String} type - Name of the data
 */
export const createAsyncMutation = type => ({
  BASE: `${type}`,
  SUCCESS: `${type}_SUCCESS`,
  FAILURE: `${type}_FAILURE`,
  PENDING: `${type}_PENDING`,
  loadingKey: `${camelCase(type)}Pending`,
  statusCode: `${camelCase(type)}StatusCode`,
  stateKey: `${camelCase(type)}Data`,
});
/*
 * EXAMPLE:
 * createAsyncMutation('GET_INFO_ASYNC') will lead to:
 *
 * GET_INFO_ASYNC = {
 *   BASE: GET_INFO_ASYNC_BASE,
 *   SUCCESS: GET_INFO_ASYNC_SUCCESS,
 *   PENDING: GET_INFO_ASYNC_PENDING,
 *   FAILURE: GET_INFO_ASYNC_FAILURE,
 *   loadingKey: getInfoAsyncPending,
 *   errorCode: getInfoAsyncErrorCode,
 *   stateKey: getInfoAsyncData,
 * };
 *
 */

/**
 * Do API call
 * Automatically set state when pending, on success or on failure during data fetching
 * @param {Object} store - Vuex store
 * @param {Object} param1.config - Parameter pass as axios 'config' argument
 * @param {Object} param1.mutationTypes - Mutation types given by createAsyncMutation
 * @param {Function} [param1.callback] - Called on Reponse object
 *                                       Should treat response and return data.
 *                                       Or return null  to prevent updating the state
 * @param {Object} [param1.mutationStore] - Store from which use the mutation
 * @param {String} [param1.moduleName] - If necessary, prefix the mutation name with another module
 * @return {Promise} Promise returned by axios API request
 */
export const doAsync = (store, options, cancelCallback) => {
  const {
    config,
    mutationTypes,
    callback,
    mutationStore,
    moduleName,
  } = options;

  // Call the mutation of a specific module
  let mutationBaseName = mutationTypes.BASE;
  if (moduleName) {
    mutationBaseName = `${moduleName}/${mutationTypes.BASE}`;
  }

  store = mutationStore || store;
  store.commit(mutationBaseName, { type: mutationTypes.PENDING, value: true });
  return Vue.axios({
    ...config,
    cancelToken: cancelCallback ? new axios.CancelToken(cancelCallback) : undefined,
  })
    .then((response) => {
      // Success
      let data = response;
      if (callback) {
        data = callback(response);
      }
      if (data !== null) {
        // TODO Consider freezing every state object/array
        // Object.freeze(data);
        store.commit(mutationBaseName, {
          type: mutationTypes.SUCCESS,
          data,
          statusCode: response.status,
        });
      }
      store.commit(mutationBaseName, { type: mutationTypes.PENDING, value: false });
      return Promise.resolve(response);
    })
    .catch((error) => {
      // Failure
      store.commit(mutationBaseName, {
        type: mutationTypes.FAILURE,
        statusCode: error.response ? error.response.status : 0,
      });
      store.commit(mutationBaseName, { type: mutationTypes.PENDING, value: false });
      return Promise.reject(error);
    });
};

/**
 * Set async value manually
 * @param {Object} store - Vuex store
 * @param {Object} param1.mutationTypes - Mutation types given by createAsyncMutation
 * @param {Function} param1.value - Value to set
 */
export const setAsyncValue = (store, mutationTypes, value) => {
  store.commit(mutationTypes.BASE, {
    type: mutationTypes.SUCCESS,
    data: value,
    statusCode: 200,
  });
  store.commit(mutationTypes.BASE, { type: mutationTypes.PENDING, value: false });
};

/**
 * For each async mutation, dynamically add the three mutations:
 * SUCCESS - write the response to the store using the `stateKey` property
 * PENDING - set the `loadingKey` property in the store to true
 * FAILURE - Set `loadingKey` to false, and `errorCode` - for example 401, 404...
 *
 * stateKey, errorCode and loadingKey are prepended with the name of the action,
 * for example an action getData would lead to:
 *
 * mutations:
 *   GET_DATA_SUCCESS,
 *   GET_DATA_PENDING,
 *   GET_DATA_FAILURE
 *
 * state:
 *   getDataLoadingKey,
 *   getDataStateKey,
 *   getDataErrorCode
 *
 * Note we use Vue.set, simply doing state[loadingKey] will not work, because the property needs
 * to be present initially for it to be included in the Vue reactivity system. Vue.set allows us
 * to add a reactive property dynamically.
 * @param {Object} mutations - Vuex mutations object
 * @param {Object} types - Mutation types list
 */
export const compileAsyncMutations = (mutations, types) => {
  Object.keys(types).forEach((type) => {
    if (!types[type].BASE) return;
    mutations[types[type].BASE] = (state, payload) => {
      switch (payload.type) {
        case types[type].PENDING:
          Vue.set(state, types[type].loadingKey, payload.value);
          return;
        case types[type].SUCCESS:
          Vue.set(state, types[type].statusCode, payload.statusCode);
          Vue.set(state, types[type].stateKey, payload.data);
          return;
        case types[type].FAILURE:
          Vue.set(state, types[type].statusCode, payload.statusCode);
          return;
        default:
          console.error('Error: Vuex store: Async key type is incorrect');
      }
    };
  });
};

export default {
  createAsyncMutation,
  doAsync,
  setAsyncValue,
  compileAsyncMutations,
};
